/**
 * Dashboard Customization Context
 * ================================
 * Manages user preferences for dashboard widget layout,
 * visibility, and ordering with localStorage persistence.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_URL } from '@/config/apiConfig';

// Default widget configuration - ORDER MATCHES LOGICAL READING FLOW
// Top to bottom: Positive → Urgent → Actionable → Financial → Performance → Market → Planning
const DEFAULT_WIDGETS = [
  { id: 'greeting', name: 'Greeting & Quick Stats', enabled: true, order: 0, category: 'core' },
  { id: 'recent-wins', name: 'Recent Wins', enabled: true, order: 1, category: 'positive' },
  { id: 'business-concerns', name: 'Business Concerns', enabled: true, order: 2, category: 'urgent' },
  { id: 'opportunities', name: 'Opportunities', enabled: true, order: 3, category: 'actionable' },
  { id: 'revenue-forecast', name: 'Revenue Forecast', enabled: true, order: 4, category: 'financial' },
  { id: 'pl-snapshot', name: 'P&L Snapshot', enabled: true, order: 5, category: 'financial' },
  { id: 'ai-savings-finder', name: 'AI Savings Finder', enabled: true, order: 22, category: 'financial' },
  { id: 'economic-indicators', name: 'Economic Pulse', enabled: true, order: 6, category: 'market' },
  { id: 'staff-performance', name: 'Staff Performance', enabled: true, order: 7, category: 'performance' },
  { id: 'customer-sentiment', name: 'Customer Sentiment', enabled: true, order: 8, category: 'performance' },
  { id: 'competitor-intelligence', name: 'Competitor Intelligence', enabled: true, order: 9, category: 'market' },
  { id: 'supplier-performance', name: 'Supplier Performance', enabled: true, order: 10, category: 'market' },
  { id: 'weather-impact', name: 'Weather Impact', enabled: true, order: 11, category: 'planning' },
  { id: 'goal-tracker', name: 'Goal Tracker', enabled: true, order: 12, category: 'planning' },
  // Insights tab widgets
  { id: 'health-score', name: 'Client Health Score', enabled: true, order: 13, category: 'insights' },
  { id: 'what-if', name: 'What-If Scenario', enabled: true, order: 14, category: 'insights' },
  { id: 'insights-revenue-forecast', name: 'Revenue Forecasting', enabled: true, order: 15, category: 'insights' },
  { id: 'insights-goals', name: 'Goal Tracker', enabled: true, order: 16, category: 'insights' },
  { id: 'insights-weather', name: 'Weather Impact', enabled: true, order: 17, category: 'insights' },
  { id: 'insights-competitors', name: 'Competitor Intelligence', enabled: true, order: 18, category: 'insights' },
  { id: 'insights-sentiment', name: 'Customer Sentiment', enabled: true, order: 19, category: 'insights' },
  { id: 'insights-suppliers', name: 'Supplier Performance', enabled: true, order: 20, category: 'insights' },
  { id: 'insights-staff-leaderboard', name: 'Staff Leaderboard', enabled: true, order: 21, category: 'insights' },
];

const WIDGET_CATEGORIES = {
  core: { name: 'Core', color: 'violet' },
  positive: { name: 'Wins', color: 'emerald' },
  urgent: { name: 'Urgent', color: 'red' },
  actionable: { name: 'Actions', color: 'amber' },
  financial: { name: 'Financial', color: 'green' },
  performance: { name: 'Performance', color: 'blue' },
  market: { name: 'Market', color: 'purple' },
  planning: { name: 'Planning', color: 'cyan' },
  insights: { name: 'Insights', color: 'indigo' },
};

const DashboardCustomizationContext = createContext({
  widgets: DEFAULT_WIDGETS,
  categories: WIDGET_CATEGORIES,
  toggleWidget: () => {},
  reorderWidgets: () => {},
  resetToDefault: () => {},
  getEnabledWidgets: () => [],
  isWidgetEnabled: () => true,
  isPanelOpen: false,
  setIsPanelOpen: () => {},
});

export function useDashboardCustomization() {
  return useContext(DashboardCustomizationContext);
}

export function DashboardCustomizationProvider({ children, userId = 'default' }) {
  const [widgets, setWidgets] = useState(() => {
    // Load from localStorage
    const stored = localStorage.getItem(`corvana-dashboard-widgets-${userId}`);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Merge with defaults to handle new widgets
        return DEFAULT_WIDGETS.map(defaultWidget => {
          const storedWidget = parsed.find(w => w.id === defaultWidget.id);
          return storedWidget ? { ...defaultWidget, ...storedWidget } : defaultWidget;
        });
      } catch (e) {
        console.error('Failed to parse stored widgets:', e);
      }
    }
    return DEFAULT_WIDGETS;
  });
  
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // Persist to localStorage whenever widgets change
  useEffect(() => {
    localStorage.setItem(`corvana-dashboard-widgets-${userId}`, JSON.stringify(widgets));
  }, [widgets, userId]);

  const toggleWidget = useCallback((widgetId) => {
    setWidgets(prev => 
      prev.map(w => w.id === widgetId ? { ...w, enabled: !w.enabled } : w)
    );
  }, []);

  const reorderWidgets = useCallback((startIndex, endIndex) => {
    setWidgets(prev => {
      const result = Array.from(prev);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);
      // Update order values
      return result.map((w, idx) => ({ ...w, order: idx }));
    });
  }, []);

  const moveWidgetUp = useCallback((widgetId) => {
    setWidgets(prev => {
      const index = prev.findIndex(w => w.id === widgetId);
      if (index <= 0) return prev;
      const result = Array.from(prev);
      [result[index - 1], result[index]] = [result[index], result[index - 1]];
      return result.map((w, idx) => ({ ...w, order: idx }));
    });
  }, []);

  const moveWidgetDown = useCallback((widgetId) => {
    setWidgets(prev => {
      const index = prev.findIndex(w => w.id === widgetId);
      if (index >= prev.length - 1) return prev;
      const result = Array.from(prev);
      [result[index], result[index + 1]] = [result[index + 1], result[index]];
      return result.map((w, idx) => ({ ...w, order: idx }));
    });
  }, []);

  const resetToDefault = useCallback(() => {
    setWidgets(DEFAULT_WIDGETS);
    localStorage.removeItem(`corvana-dashboard-widgets-${userId}`);
  }, [userId]);

  const getEnabledWidgets = useCallback(() => {
    return widgets
      .filter(w => w.enabled)
      .sort((a, b) => a.order - b.order);
  }, [widgets]);

  const isWidgetEnabled = useCallback((widgetId) => {
    const widget = widgets.find(w => w.id === widgetId);
    return widget ? widget.enabled : true;
  }, [widgets]);

  return (
    <DashboardCustomizationContext.Provider value={{
      widgets,
      categories: WIDGET_CATEGORIES,
      toggleWidget,
      reorderWidgets,
      moveWidgetUp,
      moveWidgetDown,
      resetToDefault,
      getEnabledWidgets,
      isWidgetEnabled,
      isPanelOpen,
      setIsPanelOpen,
    }}>
      {children}
    </DashboardCustomizationContext.Provider>
  );
}

export default DashboardCustomizationProvider;
